'use client';

import React, { useState } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, ShoppingBag, QrCode, FileText, BarChart3, Star,
  CheckCircle2, ChevronDown, Sparkles, MapPin, Smile
} from 'lucide-react'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { ShinyButton } from '@/components/ui/shiny-button'
import { DashboardPreview } from '@/components/ui/dashboard-animation'

export default function SmallBusinessesClient() {
  const [openFaq, setOpenFaq] = useState<number | null>(null)

  const faqs = [
    {
      q: "Do I have to reprint QR codes when I change my digital menus or links?",
      a: "No! Dynamic QR codes point to a redirect link. You can update the target URL in your Lenklyst dashboard anytime, and the printed code will automatically direct customers to the new page."
    },
    {
      q: "How do customer feedback forms and reviews work?",
      a: "You can create customized feedback sheets, rating forms, or email collection forms in seconds. Customers scan the QR code, fill out the form on their phone, and the responses appear in your dashboard instantly."
    },
    {
      q: "Can we sell digital gift cards or services through Lenklyst?",
      a: "Yes. Using our storefront feature, you can build a digital store and accept payments with Stripe. It takes less than 5 minutes to launch a store for digital coupon vouchers or booking confirmations."
    },
    {
      q: "Is there a limit on how many dynamic scans we can track?",
      a: "No scan limits. We track scan volumes, operating systems, and scan times so you can analyze which physical flyer locations or table cards perform best."
    }
  ]



  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white font-sans">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 max-w-[1400px] mx-auto z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center min-h-[70vh]">
          {/* Left Column: Hero Copy */}
          <div className="lg:col-span-7 space-y-6 flex flex-col items-start text-left">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              <Smile className="w-3.5 h-3.5 text-primary" /> For Small &amp; Local Businesses
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.05]">
              Offline to online.<br />
              <span className="text-gray-500">Simplified marketing.</span><br />
              Grow local sales.
            </h1>
            <p className="text-base sm:text-lg text-white/50 font-light max-w-xl leading-relaxed">
              Connect offline customers to your menus, review pages, social links, and digital store. Update printed QR links instantly without spending a penny on reprinting.
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
              <Link href="/auth/sign-up" className="w-full sm:w-auto">
                <ShinyButton className="w-full justify-center py-4 px-8 text-base">
                  Grow Your Business <ArrowRight className="w-4 h-4 ml-1" />
                </ShinyButton>
              </Link>
              <a href="#benefits" className="w-full sm:w-auto text-center py-3.5 px-8 rounded-full border border-white/10 text-white/60 hover:text-white hover:border-white/20 transition-all font-semibold text-sm">
                View Local Benefits
              </a>
            </div>
          </div>

          {/* Right Column: Dashboard Preview */}
          <div className="lg:col-span-5 flex justify-center lg:justify-end relative">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
            <div className="w-full max-w-[460px] relative z-10">
              <DashboardPreview sectionId="qr" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-24 border-t border-white/5 relative z-10">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="space-y-4 mb-16 text-center">
            <span className="text-[10px] font-bold tracking-[0.2em] text-primary uppercase">Local Store Growth</span>
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Bridges the gap to digital conversions.
            </h2>
            <p className="text-sm sm:text-base text-white/40 max-w-lg mx-auto font-light">
              Simple online setups to increase foot traffic and positive customer review counts.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* QR codes */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <QrCode className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Flexible Dynamic QR Codes</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Generate print-ready QR codes for menus, brochures, sandwich boards, and receipts. Edit target links instantly whenever menu choices or store locations change.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['High-resolution vector print prints', 'Instant destination URL updates', 'Geolocation scans log files'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Customer reviews */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <FileText className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Interactive Review Forms</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Capture customer satisfaction scores and feedback directly on mobile. Build simple, fast, single-page surveys with rating boxes and comment forms.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Quick star ratings input styles', 'Direct customer email collection', 'Export reviews table as CSV logs'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Gift vouchers store */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <ShoppingBag className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Instant Storefront Vouchers</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Sell digital items or voucher coupons directly to Stripe. Customers receive printable receipts or download keys instantly on their smartphone.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Direct Stripe payouts setup', 'Platform fee of just 5%', 'Instant delivery voucher coupons'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Local metrics */}
            <div className="p-8 bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-[2rem] transition-all flex flex-col justify-between space-y-6">
              <div className="space-y-4">
                <div className="p-3 bg-white/5 rounded-2xl w-fit">
                  <BarChart3 className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold uppercase tracking-tight">Offline Scan Metrics</h3>
                <p className="text-sm text-white/50 font-light leading-relaxed">
                  Compare which physical tables, signs, or flyers drive the most interactions. Monitor scan frequency logs to measure offline marketing ROI.
                </p>
              </div>
              <ul className="space-y-2 border-t border-white/[0.04] pt-6">
                {['Track scans count per QR key', 'Mobile device types breakdowns', 'Hourly scan frequency log peaks'].map((item) => (
                  <li key={item} className="flex items-center gap-2 text-xs text-white/75 font-light">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 border-t border-white/5 relative z-10 bg-black/40">
        <div className="max-w-[800px] mx-auto px-6">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">
              Business FAQs
            </h2>
            <p className="text-xs sm:text-sm text-white/40 max-w-sm mx-auto font-light">
              Clear answers to help local business owners get started.
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, idx) => {
              const isOpen = openFaq === idx
              return (
                <div 
                  key={idx}
                  className="bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
                  >
                    <span className="text-sm font-bold uppercase tracking-tight text-white/90">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-white/40 transition-transform duration-300 ${isOpen ? "rotate-180 text-emerald-400" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-white/50 leading-relaxed font-light border-t border-white/[0.02]">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-32 border-t border-white/5 relative z-10 overflow-hidden text-center">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-emerald-500/10 rounded-full blur-[160px] pointer-events-none" />
        <div className="max-w-[1400px] mx-auto px-6 relative space-y-8">
          <h2 className="text-4xl sm:text-6xl font-bold tracking-tighter uppercase">
            Bridge your physical space with digital sales.
          </h2>
          <p className="text-sm sm:text-base text-white/40 max-w-md mx-auto font-light leading-relaxed">
            Generate dynamic QR codes and collect customer feedback forms on the fly.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/auth/sign-up">
              <ShinyButton className="px-10 py-4 text-sm font-bold">
                Deploy Business Setup Free <ArrowRight className="w-4 h-4 ml-1" />
              </ShinyButton>
            </Link>
            <Link href="/pricing" className="text-white/60 hover:text-white border border-white/10 px-8 py-3.5 rounded-full text-sm font-semibold hover:border-white/20 transition-all">
              Compare Tiers
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
